import { useState, useMemo, useCallback, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import {
    AlertTriangle, Trash2, RefreshCw, Search, ChevronDown, ChevronUp,
    ShieldCheck, Bell, Coins, Cpu, Wrench, Clock, User, Eye,
    CheckCircle, XCircle, AlertCircle, Info, BellRing, Zap,
    ArrowRight, FileText, Bot, CreditCard, Activity, BarChart3,
    Terminal, Play, Filter, Calendar, Loader2, Gauge,
} from 'lucide-react';
import {
    useAppPerformanceSummary,
    useAppPerformanceSlowSessions,
    formatPerfMs,
} from '@/hooks/useAppPerformanceAnalytics';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ErrorLog {
    id: string;
    level: string;
    message: string;
    context: string;
    user_id: string | null;
    platform: string | null;
    app_version: string | null;
    metadata: Record<string, unknown>;
    created_at: string;
    display_name: string | null;
}

interface AuditEntry {
    id: string;
    user_id: string;
    display_name: string | null;
    action: string;
    entity_type: string | null;
    entity_id: string | null;
    details: Record<string, unknown>;
    created_at: string;
}

interface NotificationEntry {
    id: string;
    user_id: string;
    display_name: string | null;
    type: string;
    title: string;
    message: string;
    data: Record<string, unknown>;
    read: boolean;
    sent: boolean;
    created_at: string;
}

interface NotificationTypeStat {
    type: string;
    count: number;
    unread_count: number;
    last_24h: number;
}

interface NotificationStats {
    total: number;
    unread: number;
    today: number;
    this_week: number;
    by_type: NotificationTypeStat[];
}

interface TokenTransaction {
    id: string;
    user_id: string;
    display_name: string | null;
    transaction_type: string;
    amount: number;
    balance_after: number;
    feature_key: string | null;
    description: string | null;
    created_at: string;
}

interface AIJob {
    id: string;
    user_id: string;
    display_name: string | null;
    capture_id: string;
    status: string;
    provider: string;
    tokens_consumed: number;
    error_message: string | null;
    prompt_used: string | null;
    created_at: string;
    processing_started_at: string | null;
    completed_at: string | null;
}

// ─── Tabs ───────────────────────────────────────────────────────────────────

type TabId = 'errors' | 'audit' | 'notifications' | 'tokens' | 'app-insights' | 'performance' | 'maintenance';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'errors', label: 'Error Logs', icon: <AlertTriangle size={16} /> },
    { id: 'audit', label: 'Auditoría', icon: <ShieldCheck size={16} /> },
    { id: 'notifications', label: 'Notificaciones', icon: <Bell size={16} /> },
    { id: 'tokens', label: 'Fishcoins & IA', icon: <Coins size={16} /> },
    { id: 'app-insights', label: 'App Insights', icon: <Activity size={16} /> },
    { id: 'performance', label: 'Rendimiento móvil', icon: <Gauge size={16} /> },
    { id: 'maintenance', label: 'Limpieza', icon: <Wrench size={16} /> },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
}

function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `hace ${days}d`;
}

const LEVEL_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
    error: { color: 'text-red-400', bg: 'bg-red-500/10', icon: <XCircle size={14} /> },
    warning: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: <AlertCircle size={14} /> },
    info: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: <Info size={14} /> },
    track: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: <Eye size={14} /> },
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
    pending: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: <Clock size={14} /> },
    processing: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: <RefreshCw size={14} className="animate-spin" /> },
    completed: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: <CheckCircle size={14} /> },
    failed: { color: 'text-red-400', bg: 'bg-red-500/10', icon: <XCircle size={14} /> },
    refunded: { color: 'text-purple-400', bg: 'bg-purple-500/10', icon: <CreditCard size={14} /> },
};

const NOTIFICATION_TYPE_COLORS: Record<string, string> = {
    like: 'text-rose-400',
    comment: 'text-blue-400',
    reply: 'text-cyan-400',
    follow: 'text-indigo-400',
    mention: 'text-amber-400',
    message: 'text-emerald-400',
    system: 'text-gray-400',
    test: 'text-gray-500',
    new_capture: 'text-teal-400',
    level_up: 'text-purple-400',
    material_approved: 'text-green-400',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function ToolsPage() {
    const [activeTab, setActiveTab] = useState<TabId>('errors');
    const queryClient = useQueryClient();

    return (
        <div className="space-y-6">
            <PageHeader
                title="Herramientas del Sistema"
                description="Diagnóstico, monitoreo y mantenimiento de la plataforma"
            />

            {/* Tab Bar */}
            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg overflow-x-auto">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                            activeTab === tab.id
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                        )}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'errors' && <ErrorLogsTab />}
            {activeTab === 'audit' && <AuditLogTab />}
            {activeTab === 'notifications' && <NotificationsTab />}
            {activeTab === 'tokens' && <TokensAndAITab />}
            {activeTab === 'app-insights' && <AppInsightsTab />}
            {activeTab === 'performance' && <AppPerformanceTab />}
            {activeTab === 'maintenance' && <MaintenanceTab queryClient={queryClient} />}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1: ERROR LOGS
// ═══════════════════════════════════════════════════════════════════════════

function ErrorLogsTab() {
    const [levelFilter, setLevelFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const { data: errorLogs = [], isLoading } = useQuery({
        queryKey: ['admin', 'error-logs', levelFilter],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 200 };
            if (levelFilter !== 'all') params.p_level = levelFilter;
            const { data, error } = await dbClient.rpc<ErrorLog[]>('admin_get_error_logs', params);
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const filtered = useMemo(() => {
        if (!searchQuery) return errorLogs;
        const q = searchQuery.toLowerCase();
        return errorLogs.filter(l =>
            l.message?.toLowerCase().includes(q) ||
            l.context?.toLowerCase().includes(q) ||
            l.display_name?.toLowerCase().includes(q)
        );
    }, [errorLogs, searchQuery]);

    const levelCounts = useMemo(() => {
        const counts: Record<string, number> = { all: errorLogs.length };
        errorLogs.forEach(l => { counts[l.level] = (counts[l.level] || 0) + 1; });
        return counts;
    }, [errorLogs]);

    return (
        <div className="space-y-4">
            {/* Header Controls */}
            <div className="flex flex-wrap items-center gap-3">
                {/* Level Filter Pills */}
                <div className="flex gap-1 p-1 bg-muted/30 rounded-lg">
                    {['all', 'error', 'warning', 'track'].map(level => (
                        <button
                            key={level}
                            onClick={() => setLevelFilter(level)}
                            className={cn(
                                'px-3 py-1 rounded-md text-xs font-medium transition-all',
                                levelFilter === level
                                    ? 'bg-card text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {level === 'all' ? 'Todos' : level.charAt(0).toUpperCase() + level.slice(1)}
                            <span className="ml-1 opacity-60">({levelCounts[level] || 0})</span>
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar en mensajes, contexto, usuario..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground border border-transparent focus:border-primary/30 focus:outline-none"
                    />
                </div>
            </div>

            {/* Results Count */}
            <p className="text-xs text-muted-foreground">
                {filtered.length} {filtered.length === 1 ? 'registro' : 'registros'}
                {searchQuery && ` (filtrado de ${errorLogs.length})`}
            </p>

            {/* Error List */}
            {isLoading ? (
                <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-16 rounded-lg bg-card animate-pulse border border-border" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={<CheckCircle size={40} />} title="Sin errores" subtitle="No hay registros que coincidan con los filtros" />
            ) : (
                <div className="space-y-1">
                    {filtered.map(log => {
                        const cfg = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info;
                        const isExpanded = expandedId === log.id;
                        return (
                            <div
                                key={log.id}
                                className={cn(
                                    'rounded-lg border border-border bg-card hover:border-primary/20 transition-colors cursor-pointer',
                                    isExpanded && 'border-primary/30'
                                )}
                                onClick={() => setExpandedId(isExpanded ? null : log.id)}
                            >
                                <div className="flex items-center gap-3 px-4 py-3">
                                    <span className={cn('p-1 rounded', cfg.bg, cfg.color)}>
                                        {cfg.icon}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-foreground truncate">{log.message}</p>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                            {log.context && <span className="truncate max-w-[200px]">{log.context}</span>}
                                            {log.display_name && (
                                                <span className="flex items-center gap-1">
                                                    <User size={10} /> {log.display_name}
                                                </span>
                                            )}
                                            {log.platform && <span className="opacity-60">{log.platform}</span>}
                                        </div>
                                    </div>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                                        {timeAgo(log.created_at)}
                                    </span>
                                    {isExpanded ? <ChevronUp size={14} className="text-muted-foreground shrink-0" /> : <ChevronDown size={14} className="text-muted-foreground shrink-0" />}
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-border px-4 py-3 space-y-2 animate-fade-in">
                                        <DetailRow label="ID" value={log.id} />
                                        <DetailRow label="Nivel" value={log.level} />
                                        <DetailRow label="Fecha" value={formatDate(log.created_at)} />
                                        <DetailRow label="Ruta" value={log.context || '—'} />
                                        <DetailRow label="Usuario" value={log.display_name || '(anónimo)'} />
                                        <DetailRow label="Plataforma" value={log.platform || '—'} />
                                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                                            <div>
                                                <span className="text-xs text-muted-foreground font-medium">Metadata:</span>
                                                <pre className="mt-1 text-xs bg-muted/30 rounded-md p-2 overflow-x-auto text-foreground/80 max-h-[200px]">
                                                    {JSON.stringify(log.metadata, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2: AUDIT LOG
// ═══════════════════════════════════════════════════════════════════════════

function AuditLogTab() {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const { data: auditLogs = [], isLoading } = useQuery({
        queryKey: ['admin', 'audit-log'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<AuditEntry[]>('admin_get_audit_log', { p_limit: 200 });
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const ACTION_ICONS: Record<string, React.ReactNode> = {
        set_subscription: <CreditCard size={14} />,
        cleanup_old_errors: <Trash2 size={14} />,
        cleanup_old_notifications: <Trash2 size={14} />,
        scan_orphan_data: <Search size={14} />,
        moderate_content: <ShieldCheck size={14} />,
        resolve_report: <CheckCircle size={14} />,
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{auditLogs.length} acciones registradas</p>
            </div>

            {isLoading ? (
                <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-14 rounded-lg bg-card animate-pulse border border-border" />
                    ))}
                </div>
            ) : auditLogs.length === 0 ? (
                <EmptyState icon={<ShieldCheck size={40} />} title="Sin acciones" subtitle="No hay registros de auditoría todavía" />
            ) : (
                <div className="space-y-1">
                    {auditLogs.map(entry => {
                        const isExpanded = expandedId === entry.id;
                        return (
                            <div
                                key={entry.id}
                                className="rounded-lg border border-border bg-card hover:border-primary/20 transition-colors cursor-pointer"
                                onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                            >
                                <div className="flex items-center gap-3 px-4 py-3">
                                    <span className="p-1.5 rounded bg-indigo-500/10 text-indigo-400">
                                        {ACTION_ICONS[entry.action] || <FileText size={14} />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-foreground">
                                            <span className="font-medium">{entry.display_name || 'Admin'}</span>
                                            <span className="text-muted-foreground"> — </span>
                                            <span className="text-primary/80">{entry.action.replace(/_/g, ' ')}</span>
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                            {entry.entity_type && (
                                                <span className="px-1.5 py-0.5 rounded bg-muted/50 text-[10px] uppercase tracking-wider">
                                                    {entry.entity_type}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                                        {timeAgo(entry.created_at)}
                                    </span>
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-border px-4 py-3 space-y-2 animate-fade-in">
                                        <DetailRow label="ID" value={entry.id} />
                                        <DetailRow label="Acción" value={entry.action} />
                                        <DetailRow label="Tipo Entidad" value={entry.entity_type || '—'} />
                                        <DetailRow label="ID Entidad" value={entry.entity_id || '—'} />
                                        <DetailRow label="Fecha" value={formatDate(entry.created_at)} />
                                        {entry.details && Object.keys(entry.details).length > 0 && (
                                            <div>
                                                <span className="text-xs text-muted-foreground font-medium">Detalles:</span>
                                                <pre className="mt-1 text-xs bg-muted/30 rounded-md p-2 overflow-x-auto text-foreground/80 max-h-[200px]">
                                                    {JSON.stringify(entry.details, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3: NOTIFICATIONS OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════

function NotificationsTab() {
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Stats
    const { data: stats } = useQuery({
        queryKey: ['admin', 'notification-stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<NotificationStats>('admin_get_notification_stats');
            if (error) throw error;
            return data;
        },
    });

    // Notifications list
    const { data: notifications = [], isLoading } = useQuery({
        queryKey: ['admin', 'notifications-overview', typeFilter],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 200 };
            if (typeFilter !== 'all') params.p_type = typeFilter;
            const { data, error } = await dbClient.rpc<NotificationEntry[]>('admin_get_notifications_overview', params);
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const filtered = useMemo(() => {
        if (!searchQuery) return notifications;
        const q = searchQuery.toLowerCase();
        return notifications.filter(n =>
            n.title?.toLowerCase().includes(q) ||
            n.message?.toLowerCase().includes(q) ||
            n.display_name?.toLowerCase().includes(q) ||
            n.type?.toLowerCase().includes(q)
        );
    }, [notifications, searchQuery]);

    const notifTypes = useMemo(() => {
        const types = new Set(notifications.map(n => n.type));
        return ['all', ...Array.from(types).sort()];
    }, [notifications]);

    return (
        <div className="space-y-4">
            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <MiniStat label="Total" value={stats.total} icon={<Bell size={14} />} color="text-ocean-400" />
                    <MiniStat label="Sin leer" value={stats.unread} icon={<BellRing size={14} />} color="text-rose-400" />
                    <MiniStat label="Hoy" value={stats.today} icon={<Clock size={14} />} color="text-amber-400" />
                    <MiniStat label="Esta semana" value={stats.this_week} icon={<Zap size={14} />} color="text-emerald-400" />
                </div>
            )}

            {/* Stats by Type */}
            {stats?.by_type && stats.by_type.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Distribución por tipo</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {stats.by_type.map(t => (
                            <div key={t.type} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/20">
                                <span className={cn('text-sm font-medium capitalize', NOTIFICATION_TYPE_COLORS[t.type] || 'text-foreground')}>
                                    {t.type}
                                </span>
                                <div className="text-right">
                                    <span className="text-sm font-bold text-foreground">{t.count}</span>
                                    {t.unread_count > 0 && (
                                        <span className="ml-1.5 text-[10px] text-rose-400">({t.unread_count} ●)</span>
                                    )}
                                    {t.last_24h > 0 && (
                                        <p className="text-[10px] text-emerald-400">+{t.last_24h} hoy</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1 p-1 bg-muted/30 rounded-lg overflow-x-auto">
                    {notifTypes.map(type => (
                        <button
                            key={type}
                            onClick={() => setTypeFilter(type)}
                            className={cn(
                                'px-3 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap capitalize',
                                typeFilter === type
                                    ? 'bg-card text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {type === 'all' ? 'Todos' : type}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar notificaciones..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground border border-transparent focus:border-primary/30 focus:outline-none"
                    />
                </div>
            </div>

            {/* List */}
            <p className="text-xs text-muted-foreground">{filtered.length} notificaciones</p>

            {isLoading ? (
                <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-14 rounded-lg bg-card animate-pulse border border-border" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={<Bell size={40} />} title="Sin notificaciones" subtitle="No hay notificaciones que coincidan" />
            ) : (
                <div className="space-y-1">
                    {filtered.map(n => (
                        <div key={n.id} className="rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/20 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className={cn('w-2 h-2 rounded-full shrink-0', n.read ? 'bg-muted-foreground/30' : 'bg-rose-400')} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={cn('text-xs font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded', NOTIFICATION_TYPE_COLORS[n.type] || 'text-foreground', 'bg-muted/50')}>
                                            {n.type}
                                        </span>
                                        <span className="text-sm font-medium text-foreground truncate">{n.title}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">{n.message}</p>
                                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground/60 mt-1">
                                        <span className="flex items-center gap-1"><User size={10} /> {n.display_name || 'Sin usuario'}</span>
                                        {n.sent && <span className="text-emerald-400/60">✓ push enviado</span>}
                                    </div>
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                                    {timeAgo(n.created_at)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 4: TOKENS & AI JOBS
// ═══════════════════════════════════════════════════════════════════════════

function TokensAndAITab() {
    const [subTab, setSubTab] = useState<'transactions' | 'jobs'>('transactions');

    return (
        <div className="space-y-4">
            <div className="flex gap-1 p-1 bg-muted/30 rounded-lg w-fit">
                <button
                    onClick={() => setSubTab('transactions')}
                    className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                        subTab === 'transactions' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    <Coins size={14} /> Transacciones de Fishcoins
                </button>
                <button
                    onClick={() => setSubTab('jobs')}
                    className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                        subTab === 'jobs' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    <Bot size={14} /> Jobs de Video IA
                </button>
            </div>

            {subTab === 'transactions' ? <TokenTransactionsSection /> : <AIJobsSection />}
        </div>
    );
}

function TokenTransactionsSection() {
    const { data: transactions = [], isLoading } = useQuery({
        queryKey: ['admin', 'token-transactions'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<TokenTransaction[]>('admin_get_token_transactions', { p_limit: 200 });
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    // Aggregate stats
    const stats = useMemo(() => {
        let consumed = 0, granted = 0, refunded = 0;
        transactions.forEach(t => {
            if (t.transaction_type === 'consume') consumed += Math.abs(t.amount);
            else if (t.transaction_type === 'grant' || t.transaction_type === 'monthly_grant') granted += t.amount;
            else if (t.transaction_type === 'refund') refunded += t.amount;
        });
        return { consumed, granted, refunded, total: transactions.length };
    }, [transactions]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniStat label="Transacciones" value={stats.total} icon={<FileText size={14} />} color="text-blue-400" />
                <MiniStat label="Fishcoins Otorgados" value={stats.granted} icon={<Zap size={14} />} color="text-emerald-400" />
                <MiniStat label="Fishcoins Consumidos" value={stats.consumed} icon={<Coins size={14} />} color="text-amber-400" />
                <MiniStat label="Reembolsados" value={stats.refunded} icon={<CreditCard size={14} />} color="text-purple-400" />
            </div>

            {isLoading ? (
                <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-card animate-pulse border border-border" />)}</div>
            ) : transactions.length === 0 ? (
                <EmptyState icon={<Coins size={40} />} title="Sin transacciones" subtitle="No hay transacciones de Fishcoins registradas" />
            ) : (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Usuario</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tipo</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cantidad</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Balance</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Feature</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map(t => (
                                <tr key={t.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                                    <td className="px-4 py-2.5 text-foreground font-medium truncate max-w-[150px]">{t.display_name || '—'}</td>
                                    <td className="px-4 py-2.5">
                                        <span className={cn(
                                            'px-2 py-0.5 rounded text-xs font-medium',
                                            t.transaction_type === 'consume' ? 'bg-amber-500/10 text-amber-400' :
                                                t.transaction_type === 'refund' ? 'bg-purple-500/10 text-purple-400' :
                                                    'bg-emerald-500/10 text-emerald-400'
                                        )}>
                                            {t.transaction_type}
                                        </span>
                                    </td>
                                    <td className={cn('px-4 py-2.5 text-right font-mono font-bold', t.amount < 0 ? 'text-red-400' : 'text-emerald-400')}>
                                        {t.amount > 0 ? '+' : ''}{t.amount}
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-foreground/70">{t.balance_after}</td>
                                    <td className="px-4 py-2.5 text-muted-foreground text-xs truncate max-w-[120px]">{t.feature_key || '—'}</td>
                                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground whitespace-nowrap">{timeAgo(t.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AIJobsSection() {
    const { data: jobs = [], isLoading } = useQuery({
        queryKey: ['admin', 'ai-jobs'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<AIJob[]>('admin_get_ai_jobs_overview', { p_limit: 200 });
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const statusCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        jobs.forEach(j => { counts[j.status] = (counts[j.status] || 0) + 1; });
        return counts;
    }, [jobs]);

    const [expandedId, setExpandedId] = useState<string | null>(null);

    return (
        <div className="space-y-4">
            {/* Status summary */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(statusCounts).map(([status, count]) => {
                    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                    return (
                        <div key={status} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg', cfg.bg)}>
                            <span className={cfg.color}>{cfg.icon}</span>
                            <div>
                                <p className="text-xs text-muted-foreground capitalize">{status}</p>
                                <p className="text-sm font-bold text-foreground">{count}</p>
                            </div>
                        </div>
                    );
                })}
                {Object.keys(statusCounts).length === 0 && !isLoading && (
                    <p className="col-span-full text-xs text-muted-foreground">Sin jobs registrados</p>
                )}
            </div>

            {/* Jobs list */}
            {isLoading ? (
                <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-card animate-pulse border border-border" />)}</div>
            ) : jobs.length === 0 ? (
                <EmptyState icon={<Bot size={40} />} title="Sin jobs" subtitle="No hay trabajos de generación de video IA" />
            ) : (
                <div className="space-y-1">
                    {jobs.map(job => {
                        const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
                        const isExpanded = expandedId === job.id;
                        return (
                            <div
                                key={job.id}
                                className="rounded-lg border border-border bg-card hover:border-primary/20 transition-colors cursor-pointer"
                                onClick={() => setExpandedId(isExpanded ? null : job.id)}
                            >
                                <div className="flex items-center gap-3 px-4 py-3">
                                    <span className={cn('p-1.5 rounded', cfg.bg, cfg.color)}>{cfg.icon}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-foreground">{job.display_name || 'Sin nombre'}</span>
                                            <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider', cfg.bg, cfg.color)}>
                                                {job.status}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                            <span>{job.provider}</span>
                                            <span>•</span>
                                            <span>{job.tokens_consumed} Fishcoins</span>
                                            {job.error_message && (
                                                <>
                                                    <span>•</span>
                                                    <span className="text-red-400 truncate max-w-[250px]">{job.error_message}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">{timeAgo(job.created_at)}</span>
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-border px-4 py-3 space-y-2 animate-fade-in">
                                        <DetailRow label="Job ID" value={job.id} />
                                        <DetailRow label="Capture ID" value={job.capture_id} />
                                        <DetailRow label="Creado" value={formatDate(job.created_at)} />
                                        <DetailRow label="Inicio procesamiento" value={formatDate(job.processing_started_at)} />
                                        <DetailRow label="Completado" value={formatDate(job.completed_at)} />
                                        {job.prompt_used && (
                                            <div>
                                                <span className="text-xs text-muted-foreground font-medium">Prompt:</span>
                                                <p className="mt-1 text-xs bg-muted/30 rounded-md p-2 text-foreground/80">{job.prompt_used}</p>
                                            </div>
                                        )}
                                        {job.error_message && (
                                            <div>
                                                <span className="text-xs text-red-400 font-medium">Error:</span>
                                                <p className="mt-1 text-xs bg-red-500/10 rounded-md p-2 text-red-300">{job.error_message}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 5: MAINTENANCE
// ═══════════════════════════════════════════════════════════════════════════

interface MaintenanceAction {
    id: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    dangerous?: boolean;
}

const MAINTENANCE_ACTIONS: MaintenanceAction[] = [
    {
        id: 'old_errors',
        label: 'Limpiar Error Logs Antiguos',
        description: 'Elimina registros de error con más de 30 días de antigüedad.',
        icon: <Trash2 size={18} />,
        color: 'text-amber-400',
    },
    {
        id: 'old_notifications',
        label: 'Limpiar Notificaciones Leídas',
        description: 'Elimina notificaciones leídas con más de 60 días de antigüedad.',
        icon: <Bell size={18} />,
        color: 'text-blue-400',
    },
    {
        id: 'orphan_data',
        label: 'Escanear Datos Huérfanos',
        description: 'Detecta registros que referencian usuarios eliminados. No elimina nada (dry run).',
        icon: <Search size={18} />,
        color: 'text-emerald-400',
    },
];

function MaintenanceTab({ queryClient }: { queryClient: ReturnType<typeof useQueryClient> }) {
    const [results, setResults] = useState<Record<string, { data: any; error: string | null }>>({});
    const [runningAction, setRunningAction] = useState<string | null>(null);

    const runAction = useCallback(async (actionId: string) => {
        setRunningAction(actionId);
        try {
            const { data, error } = await dbClient.rpc<any>(`admin_cleanup_${actionId}`);
            if (error) {
                setResults(prev => ({ ...prev, [actionId]: { data: null, error: error.message } }));
            } else {
                setResults(prev => ({ ...prev, [actionId]: { data, error: null } }));
                queryClient.invalidateQueries({ queryKey: ['admin'] });
            }
        } catch (err: any) {
            setResults(prev => ({ ...prev, [actionId]: { data: null, error: err.message } }));
        } finally {
            setRunningAction(null);
        }
    }, [queryClient]);

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="flex items-start gap-3">
                    <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">Acciones de limpieza</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Estas acciones limpian datos antiguos u obsoletos de la base de datos.
                            Todas las acciones quedan registradas en el log de auditoría.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid gap-4">
                {MAINTENANCE_ACTIONS.map(action => {
                    const result = results[action.id];
                    const isRunning = runningAction === action.id;

                    return (
                        <div key={action.id} className="rounded-xl border border-border bg-card p-5">
                            <div className="flex items-start gap-4">
                                <div className={cn('p-2.5 rounded-lg bg-muted/50', action.color)}>
                                    {action.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-foreground">{action.label}</h4>
                                    <p className="text-xs text-muted-foreground mt-1">{action.description}</p>

                                    {/* Result */}
                                    {result && (
                                        <div className={cn(
                                            'mt-3 p-3 rounded-lg text-xs',
                                            result.error ? 'bg-red-500/10 text-red-300' : 'bg-emerald-500/10 text-emerald-300'
                                        )}>
                                            {result.error ? (
                                                <span>❌ Error: {result.error}</span>
                                            ) : (
                                                <pre className="overflow-x-auto whitespace-pre-wrap">
                                                    {JSON.stringify(result.data, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => runAction(action.id)}
                                    disabled={isRunning}
                                    className={cn(
                                        'px-4 py-2 rounded-lg text-sm font-medium transition-all shrink-0',
                                        'bg-primary/10 text-primary hover:bg-primary/20',
                                        isRunning && 'opacity-50 cursor-not-allowed'
                                    )}
                                >
                                    {isRunning ? (
                                        <RefreshCw size={14} className="animate-spin" />
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            Ejecutar <ArrowRight size={14} />
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: APPLICATION INSIGHTS
// ═══════════════════════════════════════════════════════════════════════════

interface AppInsightsResult {
    columns: { name: string; type: string }[];
    results: Record<string, any>[];
    count: number;
    query: string;
    timespan: string;
}

const AI_TEMPLATES: { id: string; label: string; description: string; icon: React.ReactNode; needsFilter?: boolean }[] = [
    { id: 'health-overview', label: 'Estado General', description: 'Resumen de salud del sistema', icon: <Activity size={14} /> },
    { id: 'requests-by-endpoint', label: 'Requests por Endpoint', description: 'Volumen, duración y tasa de error por función', icon: <BarChart3 size={14} /> },
    { id: 'recent-exceptions', label: 'Excepciones Recientes', description: 'Últimas excepciones detectadas', icon: <XCircle size={14} /> },
    { id: 'exception-summary', label: 'Resumen Excepciones', description: 'Excepciones agrupadas por tipo y mensaje', icon: <AlertTriangle size={14} /> },
    { id: 'failed-requests', label: 'Requests Fallidas', description: 'Peticiones HTTP con error', icon: <AlertCircle size={14} /> },
    { id: 'requests-by-function', label: 'Buscar Función', description: 'Buscar requests de una función específica', icon: <Search size={14} />, needsFilter: true },
    { id: 'traces-by-function', label: 'Traces por Función', description: 'Logs/traces de una función específica', icon: <FileText size={14} />, needsFilter: true },
    { id: 'slow-requests', label: 'Requests Lentas', description: 'Peticiones con duración > 5 segundos', icon: <Clock size={14} /> },
    { id: 'dependencies-summary', label: 'Dependencias', description: 'Llamadas externas (BD, blob, etc.)', icon: <ArrowRight size={14} /> },
    { id: 'request-timeline', label: 'Timeline Diario', description: 'Volumen de requests por día', icon: <Calendar size={14} /> },
    // Stripe audit
    { id: 'stripe-requests', label: 'Stripe: Requests', description: 'Volumen y errores en funciones Stripe', icon: <CreditCard size={14} /> },
    { id: 'stripe-exceptions', label: 'Stripe: Excepciones', description: 'Excepciones en checkout/verify/sync/webhook', icon: <XCircle size={14} /> },
    { id: 'stripe-traces', label: 'Stripe: Traces', description: 'Logs (localhost, success_url, redirect)', icon: <FileText size={14} /> },
    { id: 'stripe-failed-urls', label: 'Stripe: URLs fallidas', description: 'URLs en requests fallidas (localhost?)', icon: <AlertCircle size={14} /> },
    { id: 'stripe-dependencies', label: 'Stripe: Dependencias', description: 'Llamadas a API Stripe externa', icon: <ArrowRight size={14} /> },
    { id: 'custom', label: 'Query Personalizada', description: 'Escribir KQL manualmente', icon: <Terminal size={14} /> },
];

const AI_TIMESPANS = [
    { value: '1h', label: '1 hora' },
    { value: '6h', label: '6 horas' },
    { value: '12h', label: '12 horas' },
    { value: '1d', label: '1 día' },
    { value: '3d', label: '3 días' },
    { value: '7d', label: '7 días' },
    { value: '14d', label: '14 días' },
    { value: '30d', label: '30 días' },
];

// Lista de Azure Functions del proyecto (para selector de logs)
const AZURE_FUNCTIONS = [
    'admin-app-insights-query', 'aliexpress-generate-link', 'aliexpress-get-product', 'aliexpress-test-stats',
    'auth-google', 'auth-google-callback', 'auth-logout', 'auth-me', 'auth-refresh', 'batch-weather-update',
    'cleanup-expired-short-urls', 'create-capture', 'create-notification', 'create-trip', 'db-query',
    'delete-capture', 'delete-trip', 'extract-product-data', 'generate-ai-video', 'get-aemet-data',
    'get-ai-video-for-capture', 'get-ai-video-status', 'get-copernicus-sst', 'get-feature-flags',
    'get-mapbox-token', 'get-pescadillo-activity', 'get-species-activity', 'get-subscription-plans',
    'get-tide-data', 'get-weather-conditions', 'get-weather-data', 'health', 'keep-warm', 'link-preview',
    'migrate-base64-to-storage', 'migrate-heavy-base64', 'migrate-missing-capture-media', 'normalize-species-names',
    'notification-processor', 'notify-outdated-versions', 'og-image', 'og-meta',     'pescadillo-best-day-push',
    'process-ai-video-jobs', 'process-unsent-notifications', 'proxy-image',
    'refresh-materialized-view', 'regenerate-pvp-energy', 'rpc-query', 'send-error-alert', 'send-push-notification',
    'storage-download', 'storage-generate-upload-url', 'storage-public', 'storage-remove', 'storage-signed-url',
    'storage-upload', 'stripe-create-checkout', 'stripe-customer-portal', 'stripe-sync-subscription',
    'stripe-verify-session', 'stripe-webhook', 'update-capture', 'update-captures-astronomy', 'update-profile',
    'update-trip', 'youtube-channel-preview', 'youtube-channel-sync', 'youtube-hourly-sync',
];

// ═══════════════════════════════════════════════════════════════════════════
// SECCIÓN: LOGS POR FUNCIÓN
// ═══════════════════════════════════════════════════════════════════════════

function FunctionLogsSection() {
    const [selectedFunction, setSelectedFunction] = useState('');
    const [useCustomDate, setUseCustomDate] = useState(false);
    const [timespan, setTimespan] = useState('1d');
    const [expandedLogIdx, setExpandedLogIdx] = useState<number | null>(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [messageFilter, setMessageFilter] = useState('');
    const [triggerKey, setTriggerKey] = useState(0);

    const { data: result, isLoading, error, isFetching } = useQuery<AppInsightsResult>({
        queryKey: ['admin', 'app-insights', 'function-logs', selectedFunction, timespan, startDate, endDate, messageFilter, triggerKey],
        queryFn: async () => {
            const body: Record<string, string> = {
                templateId: 'function-logs-unified',
                filter: selectedFunction,
            };
            if (useCustomDate) {
                body.startDate = `${startDate}T00:00:00.000Z`;
                body.endDate = `${endDate}T23:59:59.999Z`;
            } else {
                body.timespan = timespan;
            }
            if (messageFilter.trim()) {
                body.messageFilter = messageFilter.trim();
            }
            const res = await apiClient.post<AppInsightsResult>('/app-insights-query', body);
            if (res.error) throw new Error(res.error.message);
            return res.data!;
        },
        enabled: triggerKey > 0 && !!selectedFunction,
        retry: false,
        staleTime: 30_000,
    });

    const handleExecute = () => {
        setExpandedLogIdx(null);
        setTriggerKey(k => k + 1);
    };
    const toggleLogDetail = (idx: number) => setExpandedLogIdx(prev => (prev === idx ? null : idx));

    const formatCell = (value: any, colName: string): string => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'number') return value.toLocaleString();
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) return formatDate(value);
        if (typeof value === 'boolean') return value ? '✓' : '✗';
        return String(value);
    };

    const getLogTypeBadge = (logType: string) => {
        switch (logType?.toLowerCase()) {
            case 'trace':
                return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">trace</span>;
            case 'request':
                return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-400">request</span>;
            case 'exception':
                return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400">exception</span>;
            default:
                return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">{logType || '—'}</span>;
        }
    };

    return (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div className="flex items-center gap-2">
                <FileText size={18} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Logs por función</h3>
                <span className="text-[10px] text-muted-foreground">— requests, traces y excepciones unificados</span>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[200px]">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                        Función
                    </label>
                    <select
                        value={selectedFunction}
                        onChange={e => setSelectedFunction(e.target.value)}
                        className="w-full px-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground border border-transparent focus:border-primary/30 focus:outline-none cursor-pointer"
                    >
                        <option value="">Seleccionar función...</option>
                        {AZURE_FUNCTIONS.map(fn => (
                            <option key={fn} value={fn}>{fn}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                        <input
                            type="radio"
                            name="dateMode"
                            checked={!useCustomDate}
                            onChange={() => setUseCustomDate(false)}
                            className="mr-1.5"
                        />
                        Preset
                    </label>
                    <select
                        value={timespan}
                        onChange={e => setTimespan(e.target.value)}
                        disabled={useCustomDate}
                        className="px-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground border border-transparent focus:border-primary/30 focus:outline-none cursor-pointer disabled:opacity-50"
                    >
                        {AI_TIMESPANS.map(ts => (
                            <option key={ts.value} value={ts.value}>{ts.label}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                        <input
                            type="radio"
                            name="dateMode"
                            checked={useCustomDate}
                            onChange={() => setUseCustomDate(true)}
                            className="mr-1.5"
                        />
                        Fecha personalizada
                    </label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        disabled={!useCustomDate}
                        className="px-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground border border-transparent focus:border-primary/30 focus:outline-none disabled:opacity-50 w-[140px]"
                    />
                    <span className="text-muted-foreground text-xs">→</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        disabled={!useCustomDate}
                        className="px-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground border border-transparent focus:border-primary/30 focus:outline-none disabled:opacity-50 w-[140px]"
                    />
                </div>

                <div className="flex-1 min-w-[180px]">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                        Filtrar en logs (texto libre)
                    </label>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={messageFilter}
                            onChange={e => setMessageFilter(e.target.value)}
                            placeholder="ej: error, userId, 500..."
                            className="w-full pl-9 pr-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground border border-transparent focus:border-primary/30 focus:outline-none"
                        />
                    </div>
                </div>

                <button
                    onClick={handleExecute}
                    disabled={!selectedFunction || isLoading || isFetching}
                    className={cn(
                        'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all',
                        'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                >
                    {isLoading || isFetching ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                    Buscar logs
                </button>
            </div>

            {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3">
                    <div className="flex items-center gap-2 text-red-400">
                        <XCircle size={14} />
                        <span className="text-xs">{(error as Error).message}</span>
                    </div>
                </div>
            )}

            {result && (
                <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                        {result.count} {result.count === 1 ? 'log' : 'logs'} encontrados
                    </p>
                    {result.results.length > 0 ? (
                        <div className="rounded-lg border border-border bg-card overflow-hidden max-h-[400px] overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-muted/50 border-b border-border">
                                    <tr>
                                        <th className="w-8 px-2" />
                                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Fecha</th>
                                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Tipo</th>
                                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Función</th>
                                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-muted-foreground uppercase">Mensaje</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.results.map((row: Record<string, any>, idx: number) => {
                                        const isExpanded = expandedLogIdx === idx;
                                        return (
                                            <Fragment key={idx}>
                                                <tr
                                                    onClick={() => toggleLogDetail(idx)}
                                                    className={cn(
                                                        'border-b border-border/40 hover:bg-muted/20 cursor-pointer transition-colors',
                                                        row.logType === 'exception' && 'bg-red-500/5',
                                                        isExpanded && 'bg-muted/30'
                                                    )}
                                                >
                                                    <td className="px-3 py-2 w-8">
                                                        {isExpanded ? <ChevronUp size={12} className="text-muted-foreground" /> : <ChevronDown size={12} className="text-muted-foreground" />}
                                                    </td>
                                                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                                                        {formatCell(row.timestamp, 'timestamp')}
                                                    </td>
                                                    <td className="px-3 py-2">{getLogTypeBadge(row.logType)}</td>
                                                    <td className="px-3 py-2 font-medium truncate max-w-[180px]" title={row.operation_Name}>
                                                        {row.operation_Name || '—'}
                                                    </td>
                                                    <td className="px-3 py-2 text-foreground/90 max-w-[400px] truncate" title={String(row.message ?? '')}>
                                                        {row.message || row.extra || '—'}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-muted/10 border-b border-border/40">
                                                        <td colSpan={5} className="px-4 py-3">
                                                            <div className="space-y-2 text-xs">
                                                                <div>
                                                                    <span className="text-muted-foreground font-medium block mb-0.5">Mensaje completo:</span>
                                                                    <pre className="p-3 rounded bg-[#1a1a2e] text-foreground overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px]">
                                                                        {String(row.message ?? '—')}
                                                                    </pre>
                                                                </div>
                                                                {row.extra && (
                                                                    <div>
                                                                        <span className="text-muted-foreground font-medium block mb-0.5">Detalles:</span>
                                                                        <pre className="p-3 rounded bg-[#1a1a2e] text-foreground overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px]">
                                                                            {String(row.extra)}
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-xs text-muted-foreground py-4">No hay logs para esta función en el periodo seleccionado.</p>
                    )}
                </div>
            )}

            {triggerKey === 0 && (
                <p className="text-[11px] text-muted-foreground">
                    Selecciona una función y pulsa "Buscar logs" para ver requests, traces (console.log) y excepciones.
                </p>
            )}
        </div>
    );
}

function AppInsightsTab() {
    const [selectedTemplate, setSelectedTemplate] = useState('health-overview');
    const [timespan, setTimespan] = useState('7d');
    const [filter, setFilter] = useState('');
    const [customQuery, setCustomQuery] = useState(
        'requests\n| where timestamp > ago(7d)\n| summarize count() by name\n| order by count_ desc\n| take 20'
    );
    const [triggerKey, setTriggerKey] = useState(0);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const templateConfig = AI_TEMPLATES.find(t => t.id === selectedTemplate);

    const { data: result, isLoading, error, isFetching } = useQuery<AppInsightsResult>({
        queryKey: ['admin', 'app-insights', selectedTemplate, timespan, filter, customQuery, triggerKey],
        queryFn: async () => {
            const body: Record<string, string> = {
                templateId: selectedTemplate,
                timespan,
            };
            if (templateConfig?.needsFilter && filter) {
                body.filter = filter;
            }
            if (selectedTemplate === 'custom') {
                body.customQuery = customQuery;
            }
            const res = await apiClient.post<AppInsightsResult>('/app-insights-query', body);
            if (res.error) throw new Error(res.error.message);
            return res.data!;
        },
        enabled: triggerKey > 0,
        retry: false,
        staleTime: 30_000,
    });

    const handleExecute = () => {
        setExpandedRows(new Set());
        setTriggerKey(k => k + 1);
    };

    const handleTemplateChange = (id: string) => {
        setSelectedTemplate(id);
        setExpandedRows(new Set());
    };

    const toggleRow = (idx: number) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    // Format cell values for display
    const formatCell = (value: any, colName: string): string => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'number') {
            if (colName.includes('duration') || colName.includes('p95')) return `${value.toFixed(1)}ms`;
            if (colName.includes('rate')) return `${value}%`;
            return value.toLocaleString();
        }
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
            return formatDate(value);
        }
        if (typeof value === 'boolean') return value ? '✓' : '✗';
        return String(value);
    };

    // Determine which columns to show as "main" in the table vs expanded detail
    const mainColumns = useMemo(() => {
        if (!result?.columns) return [];
        // Show max 6 columns in table, rest in expanded view
        return result.columns.slice(0, 6);
    }, [result?.columns]);

    const extraColumns = useMemo(() => {
        if (!result?.columns || result.columns.length <= 6) return [];
        return result.columns.slice(6);
    }, [result?.columns]);

    return (
        <div className="space-y-4">
            {/* Logs por función — vista dedicada */}
            <FunctionLogsSection />

            {/* Controls — resto de consultas */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                {/* Template Selector */}
                <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                        Tipo de consulta
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                        {AI_TEMPLATES.map(t => (
                            <button
                                key={t.id}
                                onClick={() => handleTemplateChange(t.id)}
                                className={cn(
                                    'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all text-left',
                                    selectedTemplate === t.id
                                        ? 'bg-primary/10 text-primary border border-primary/30'
                                        : 'bg-muted/20 text-muted-foreground hover:bg-muted/40 hover:text-foreground border border-transparent'
                                )}
                            >
                                <span className="shrink-0">{t.icon}</span>
                                <span className="truncate">{t.label}</span>
                            </button>
                        ))}
                    </div>
                    {templateConfig && (
                        <p className="text-[11px] text-muted-foreground/70 mt-1.5 ml-1">{templateConfig.description}</p>
                    )}
                </div>

                {/* Timespan + Filter + Execute */}
                <div className="flex flex-wrap items-end gap-3">
                    {/* Timespan */}
                    <div className="min-w-[140px]">
                        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                            Periodo
                        </label>
                        <select
                            value={timespan}
                            onChange={e => setTimespan(e.target.value)}
                            className="w-full px-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground border border-transparent focus:border-primary/30 focus:outline-none cursor-pointer"
                        >
                            {AI_TIMESPANS.map(ts => (
                                <option key={ts.value} value={ts.value}>{ts.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Filter (for function-specific templates) */}
                    {templateConfig?.needsFilter && (
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                                Nombre de función
                            </label>
                            <div className="relative">
                                <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    type="text"
                                    value={filter}
                                    onChange={e => setFilter(e.target.value)}
                                    placeholder="ej: storage-upload, create-capture..."
                                    className="w-full pl-9 pr-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground border border-transparent focus:border-primary/30 focus:outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Execute */}
                    <button
                        onClick={handleExecute}
                        disabled={isLoading || isFetching || (templateConfig?.needsFilter && !filter) || (selectedTemplate === 'custom' && !customQuery.trim())}
                        className={cn(
                            'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all',
                            'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
                            'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                    >
                        {isLoading || isFetching ? (
                            <RefreshCw size={14} className="animate-spin" />
                        ) : (
                            <Play size={14} />
                        )}
                        Ejecutar
                    </button>
                </div>

                {/* Custom Query Editor */}
                {selectedTemplate === 'custom' && (
                    <div>
                        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                            KQL Query
                        </label>
                        <textarea
                            value={customQuery}
                            onChange={e => setCustomQuery(e.target.value)}
                            rows={6}
                            spellCheck={false}
                            className="w-full px-4 py-3 bg-[#1a1a2e] rounded-lg text-sm text-emerald-300 font-mono border border-border focus:border-primary/40 focus:outline-none resize-y placeholder:text-muted-foreground/40"
                            placeholder="Escribe tu query KQL aquí..."
                        />
                        <p className="text-[10px] text-muted-foreground/60 mt-1">
                            Tablas disponibles: requests, traces, exceptions, dependencies, customEvents
                        </p>
                    </div>
                )}
            </div>

            {/* Results */}
            {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3">
                    <div className="flex items-center gap-2 text-red-400">
                        <XCircle size={16} />
                        <span className="text-sm font-medium">Error</span>
                    </div>
                    <p className="text-xs text-red-300/80 mt-1">{(error as Error).message}</p>
                </div>
            )}

            {triggerKey === 0 && !result && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <Activity size={48} className="text-muted-foreground/20 mb-4" />
                    <h3 className="text-sm font-semibold text-foreground">Application Insights</h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-md">
                        Selecciona un tipo de consulta y pulsa "Ejecutar" para consultar los logs de producción de Azure Functions.
                    </p>
                </div>
            )}

            {result && (
                <div className="space-y-3">
                    {/* Result Meta */}
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                            {result.count} {result.count === 1 ? 'resultado' : 'resultados'}
                            <span className="opacity-60 ml-2">· periodo: {result.timespan}</span>
                        </p>
                        <span className="text-[10px] text-muted-foreground/50 font-mono truncate max-w-[300px]">
                            {result.query}
                        </span>
                    </div>

                    {/* Health Overview Cards */}
                    {selectedTemplate === 'health-overview' && result.results.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                            {Object.entries(result.results[0]).map(([key, value]) => {
                                const label = key.replace(/_/g, ' ');
                                const isFailure = key.includes('fail');
                                return (
                                    <div key={key} className={cn(
                                        'rounded-lg border px-3 py-2.5',
                                        isFailure && Number(value) > 0
                                            ? 'border-red-500/30 bg-red-500/5'
                                            : 'border-border bg-card/50'
                                    )}>
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">{label}</p>
                                        <p className={cn(
                                            'text-lg font-bold mt-0.5',
                                            isFailure && Number(value) > 0 ? 'text-red-400' : 'text-foreground'
                                        )}>
                                            {formatCell(value, key)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Results Table */}
                    {selectedTemplate !== 'health-overview' && result.results.length > 0 && (
                        <div className="rounded-xl border border-border bg-card overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border bg-muted/30">
                                            <th className="w-8 px-2" />
                                            {mainColumns.map(col => (
                                                <th
                                                    key={col.name}
                                                    className="text-left px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap"
                                                >
                                                    {col.name.replace(/_/g, ' ')}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.results.map((row, idx) => {
                                            const isExpanded = expandedRows.has(idx);
                                            const hasSuccess = 'success' in row;
                                            const isFailed = hasSuccess && row.success === false;
                                            return (
                                                <>
                                                    <tr
                                                        key={idx}
                                                        onClick={() => extraColumns.length > 0 && toggleRow(idx)}
                                                        className={cn(
                                                            'border-b border-border/40 transition-colors',
                                                            isFailed ? 'bg-red-500/5' : 'hover:bg-muted/10',
                                                            extraColumns.length > 0 && 'cursor-pointer'
                                                        )}
                                                    >
                                                        <td className="px-2 text-center">
                                                            {extraColumns.length > 0 && (
                                                                isExpanded
                                                                    ? <ChevronUp size={12} className="text-muted-foreground" />
                                                                    : <ChevronDown size={12} className="text-muted-foreground" />
                                                            )}
                                                        </td>
                                                        {mainColumns.map(col => (
                                                            <td
                                                                key={col.name}
                                                                className={cn(
                                                                    'px-3 py-2 whitespace-nowrap max-w-[300px] truncate',
                                                                    col.name === 'name' || col.name === 'operation_Name'
                                                                        ? 'font-medium text-foreground'
                                                                        : col.name.includes('count') || col.name === 'total' || col.name === 'failed'
                                                                            ? 'font-mono font-bold text-foreground'
                                                                            : 'text-foreground/70',
                                                                    col.name === 'failed' && Number(row[col.name]) > 0 && 'text-red-400',
                                                                    col.name === 'failure_rate' && Number(row[col.name]) > 5 && 'text-red-400',
                                                                    col.name === 'success' && row[col.name] === false && 'text-red-400',
                                                                    col.name === 'success' && row[col.name] === true && 'text-emerald-400',
                                                                    col.name === 'severityLevel' && 'text-amber-400',
                                                                )}
                                                                title={String(row[col.name] ?? '')}
                                                            >
                                                                {formatCell(row[col.name], col.name)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                    {isExpanded && extraColumns.length > 0 && (
                                                        <tr key={`${idx}-detail`} className="bg-muted/5">
                                                            <td colSpan={mainColumns.length + 1} className="px-4 py-3">
                                                                <div className="space-y-1">
                                                                    {extraColumns.map(col => (
                                                                        <div key={col.name} className="flex gap-2 text-xs">
                                                                            <span className="text-muted-foreground font-medium w-32 shrink-0">
                                                                                {col.name.replace(/_/g, ' ')}:
                                                                            </span>
                                                                            <span className="text-foreground/80 font-mono break-all">
                                                                                {formatCell(row[col.name], col.name)}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                    {/* Also show full values of all columns for long fields */}
                                                                    {mainColumns.filter(c =>
                                                                        typeof row[c.name] === 'string' && String(row[c.name]).length > 50
                                                                    ).map(col => (
                                                                        <div key={`full-${col.name}`} className="flex gap-2 text-xs pt-1 border-t border-border/30">
                                                                            <span className="text-muted-foreground font-medium w-32 shrink-0">
                                                                                {col.name} (completo):
                                                                            </span>
                                                                            <span className="text-foreground/80 font-mono break-all text-[11px]">
                                                                                {String(row[col.name])}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {result.results.length === 0 && (
                        <EmptyState
                            icon={<Activity size={40} />}
                            title="Sin resultados"
                            subtitle="La consulta no devolvió datos para el periodo seleccionado"
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// SHARED SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground font-medium w-28 shrink-0">{label}:</span>
            <span className="text-foreground/80 truncate font-mono">{value || '—'}</span>
        </div>
    );
}

function MiniStat({ label, value, icon, color }: { label: string; value: string | number; icon: React.ReactNode; color?: string }) {
    return (
        <div className="rounded-lg border border-border bg-card/50 px-3 py-2.5 flex items-center gap-2.5">
            <span className={cn('shrink-0', color || 'text-muted-foreground')}>{icon}</span>
            <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">{label}</p>
                <p className="text-sm font-bold text-foreground">{String(value)}</p>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: RENDIMIENTO MÓVIL (RUM)
// ═══════════════════════════════════════════════════════════════════════════

function AppPerformanceTab() {
    const [days, setDays] = useState(7);
    const { data: summary, isLoading, isError, refetch, isFetching } = useAppPerformanceSummary(days);
    const { data: slowSessions = [], isLoading: slowLoading } = useAppPerformanceSlowSessions(days, 30);

    const milestoneLabels: Record<string, string> = {
        app_module_loaded: 'Bundle cargado',
        update_check_done: 'Check actualización',
        capacitor_init_done: 'Capacitor init',
        react_first_render: 'Primer render React',
        auth_ready: 'Auth listo',
        feature_flags_ready: 'Feature flags',
        feed_first_paint: 'Feed visible',
        session_interactive: 'Sesión interactiva',
        blank_screen_detected: 'Pantalla en blanco',
        blank_screen_reload: 'Recarga por blanco',
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
                <Loader2 className="animate-spin" size={18} />
                Cargando métricas RUM…
            </div>
        );
    }

    if (isError || !summary) {
        return (
            <EmptyState
                icon={<Gauge size={40} />}
                title="Sin datos de rendimiento"
                subtitle="Aplica la migración app_performance_events y abre la app para generar eventos."
            />
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1 p-1 bg-muted/30 rounded-lg">
                    {[7, 14, 30].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={cn(
                                'px-3 py-1 rounded-md text-xs font-medium transition-all',
                                days === d
                                    ? 'bg-card text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {d} días
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-muted/50 hover:bg-muted text-muted-foreground"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    Actualizar
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Sesiones" value={String(summary.total_sessions)} />
                <StatCard
                    label="Feed lento (≥8s)"
                    value={String(summary.slow_feed_sessions)}
                    tone={summary.slow_feed_sessions > 0 ? 'warning' : 'default'}
                />
                <StatCard
                    label="1ª instalación p95 feed"
                    value={formatPerfMs(summary.first_launch?.feed_first_paint_p95_ms)}
                />
                <StatCard
                    label="Recurrentes p95 feed"
                    value={formatPerfMs(summary.returning?.feed_first_paint_p95_ms)}
                />
            </div>

            <section className="rounded-xl border border-border/60 bg-card/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-border/60">
                    <h3 className="text-sm font-semibold">Hitos de arranque (p50 / p95)</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-muted-foreground border-b border-border/40">
                                <th className="px-4 py-2 font-medium">Hito</th>
                                <th className="px-4 py-2 font-medium">Muestras</th>
                                <th className="px-4 py-2 font-medium">p50</th>
                                <th className="px-4 py-2 font-medium">p95</th>
                                <th className="px-4 py-2 font-medium">Máx</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary.milestones ?? []).map((m) => (
                                <tr key={m.milestone} className="border-b border-border/20 last:border-0">
                                    <td className="px-4 py-2 font-medium">
                                        {milestoneLabels[m.milestone] ?? m.milestone}
                                    </td>
                                    <td className="px-4 py-2 text-muted-foreground">{m.samples}</td>
                                    <td className="px-4 py-2">{formatPerfMs(m.p50_ms)}</td>
                                    <td className="px-4 py-2">{formatPerfMs(m.p95_ms)}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{formatPerfMs(m.max_ms)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="rounded-xl border border-border/60 bg-card/50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border/60">
                        <h3 className="text-sm font-semibold">Por plataforma (p95 feed)</h3>
                    </div>
                    <div className="divide-y divide-border/30">
                        {(summary.by_platform ?? []).length === 0 ? (
                            <p className="px-4 py-6 text-sm text-muted-foreground">Sin datos</p>
                        ) : (
                            summary.by_platform.map((p) => (
                                <div key={p.platform} className="px-4 py-3 flex justify-between gap-4 text-sm">
                                    <div>
                                        <p className="font-medium capitalize">{p.platform}</p>
                                        <p className="text-xs text-muted-foreground">{p.sessions} sesiones</p>
                                    </div>
                                    <div className="text-right text-xs space-y-0.5">
                                        <p>Feed: {formatPerfMs(p.feed_first_paint_p95_ms)}</p>
                                        <p className="text-muted-foreground">React: {formatPerfMs(p.react_first_render_p95_ms)}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                <section className="rounded-xl border border-border/60 bg-card/50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border/60">
                        <h3 className="text-sm font-semibold">Dispositivos más lentos (p95 feed)</h3>
                    </div>
                    <div className="divide-y divide-border/30 max-h-80 overflow-y-auto">
                        {(summary.slow_devices ?? []).length === 0 ? (
                            <p className="px-4 py-6 text-sm text-muted-foreground">Sin datos (mín. 3 sesiones/dispositivo)</p>
                        ) : (
                            summary.slow_devices.map((d) => (
                                <div key={`${d.platform}-${d.device_model}`} className="px-4 py-3 flex justify-between gap-4 text-sm">
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">{d.device_model}</p>
                                        <p className="text-xs text-muted-foreground capitalize">{d.platform} · {d.sessions} ses.</p>
                                    </div>
                                    <p className="font-semibold text-amber-500 shrink-0">
                                        {formatPerfMs(d.feed_first_paint_p95_ms)}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </section>
            </div>

            <section className="rounded-xl border border-border/60 bg-card/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-border/60">
                    <h3 className="text-sm font-semibold">Sesiones más lentas (feed ≥ 8s)</h3>
                </div>
                {slowLoading ? (
                    <div className="px-4 py-8 text-sm text-muted-foreground">Cargando…</div>
                ) : slowSessions.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-muted-foreground">Ninguna sesión superó el umbral en el periodo.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-muted-foreground border-b border-border/40">
                                    <th className="px-4 py-2 font-medium">Usuario</th>
                                    <th className="px-4 py-2 font-medium">Dispositivo</th>
                                    <th className="px-4 py-2 font-medium">Feed</th>
                                    <th className="px-4 py-2 font-medium">React</th>
                                    <th className="px-4 py-2 font-medium">1ª vez</th>
                                    <th className="px-4 py-2 font-medium">Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {slowSessions.map((s) => (
                                    <tr key={s.session_id} className="border-b border-border/20 last:border-0">
                                        <td className="px-4 py-2">
                                            <p className="font-medium truncate max-w-[140px]">{s.display_name ?? '—'}</p>
                                            <p className="text-xs text-muted-foreground truncate max-w-[140px]">{s.email ?? s.user_id?.slice(0, 8)}</p>
                                        </td>
                                        <td className="px-4 py-2 text-xs">
                                            <p>{s.device_model ?? '—'}</p>
                                            <p className="text-muted-foreground capitalize">{s.platform} · v{s.app_version ?? '?'}</p>
                                        </td>
                                        <td className="px-4 py-2 font-semibold text-amber-500">{formatPerfMs(s.feed_first_paint_ms)}</td>
                                        <td className="px-4 py-2 text-muted-foreground">{formatPerfMs(s.react_first_render_ms)}</td>
                                        <td className="px-4 py-2">{s.is_first_launch ? 'Sí' : 'No'}</td>
                                        <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{formatDate(s.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

function StatCard({
    label,
    value,
    tone = 'default',
}: {
    label: string;
    value: string;
    tone?: 'default' | 'warning';
}) {
    return (
        <div className="rounded-xl border border-border/60 bg-card/50 px-4 py-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={cn(
                'text-2xl font-bold mt-1',
                tone === 'warning' ? 'text-amber-500' : 'text-foreground',
            )}>
                {value}
            </p>
        </div>
    );
}

function EmptyState({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-muted-foreground/30 mb-3">{icon}</div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        </div>
    );
}
